import { Input, Check } from "@frui.ts/bootstrap";
import { observer } from "mobx-react-lite";
import React from "react";
import { Col, Form } from "react-bootstrap";
import FormGroup from "controls/formGroup";
import FormLabel from "controls/formLabel";
import DatePickerBox from "controls/datePickerBox";
import FakeInput from "@edaz/edazkit/controls/FakeInput";
import type BlacklistedEmailAddress from "entities/blacklistedEmailAddress";
import type EmailBlacklistDetailViewModel from "viewModels/administration/emailBlacklist/emailBlacklistDetailViewModel";
import type EmailBlacklistCreateViewModel from "viewModels/administration/emailBlacklist/emailBlacklistCreateViewModel";
import { formatDateTime } from "utils/localizations";
import { emptyValue } from "utils/emptyValue";

export interface EmailBlacklistFormProps {
  vm: EmailBlacklistDetailViewModel | EmailBlacklistCreateViewModel;
  isCreate?: boolean;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
const EmailBlacklistForm = observer((props: EmailBlacklistFormProps) => {
  const { vm, isCreate = false } = props;

  if (!vm.item) {
    return null;
  }

  const disabled = !vm.isEditable;
  const isDetailVm = !isCreate && "deactivateImmediately" in vm;
  const deactivateImmediately = isDetailVm && vm.deactivateImmediately;
  const isReadOnly = !isCreate && disabled;

  // Read-only detail - použij FakeInput
  if (isReadOnly) {
    const item = vm.item as BlacklistedEmailAddress;
    return (
      <>
        <Form.Row>
          <Form.Group as={Col} md={12}>
            <Form.Label>{vm.translateAttribute("emailAddressSearchPattern")}</Form.Label>
            <FakeInput target={item} property="emailAddressSearchPattern" valueFormatter={value => value ?? emptyValue} />
          </Form.Group>
        </Form.Row>
        <Form.Row>
          <Form.Group as={Col} md={12}>
            <Form.Label>{vm.translateAttribute("description")}</Form.Label>
            <FakeInput target={item} property="description" valueFormatter={value => value ?? emptyValue} />
          </Form.Group>
        </Form.Row>
        <Form.Row>
          <Form.Group as={Col} md={6}>
            <Form.Label>{vm.translateSubAttribute("emailBlacklist", "validSince")}</Form.Label>
            <FakeInput
              target={item}
              property="validSince"
              valueFormatter={value => (value ? formatDateTime(value) : emptyValue)}
            />
          </Form.Group>
          <Form.Group as={Col} md={6}>
            <Form.Label>{vm.translateSubAttribute("emailBlacklist", "validUntil")}</Form.Label>
            <FakeInput
              target={item}
              property="validUntil"
              valueFormatter={value => (value ? formatDateTime(value) : emptyValue)}
            />
          </Form.Group>
        </Form.Row>
      </>
    );
  }

  // Editace nebo vytvoření
  return (
    <>
      <Form.Row>
        <FormGroup as={Col} md={12} target={vm.item} property="emailAddressSearchPattern">
          <FormLabel required={isCreate}>{vm.translateAttribute("emailAddressSearchPattern")}</FormLabel>
          <Input disabled={!isCreate || disabled} />
        </FormGroup>
      </Form.Row>
      <Form.Row>
        <FormGroup as={Col} md={12} target={vm.item} property="description">
          <FormLabel>{vm.translateAttribute("description")}</FormLabel>
          <Input as="textarea" rows={3} disabled={disabled} />
        </FormGroup>
      </Form.Row>
      <Form.Row>
        <FormGroup as={Col} md={6} target={vm.item} property={isCreate ? "validSinceDate" : "validSince"}>
          <FormLabel required={isCreate}>{vm.translateSubAttribute("emailBlacklist", "validSince")}</FormLabel>
          {isCreate ? (
            <DatePickerBox disabled={disabled} strategyOverlay="fixed" placementOverlay="top" />
          ) : (
            <Form.Control
              disabled
              value={
                (vm.item as BlacklistedEmailAddress).validSince
                  ? formatDateTime((vm.item as BlacklistedEmailAddress).validSince)
                  : emptyValue
              }
            />
          )}
        </FormGroup>
        <FormGroup as={Col} md={6} target={vm.item} property={isCreate ? "validUntilDate" : "validUntil"}>
          <FormLabel>{vm.translateSubAttribute("emailBlacklist", "validUntil")}</FormLabel>
          {deactivateImmediately ? (
            <Form.Control disabled value={formatDateTime(new Date())} />
          ) : (
            <DatePickerBox
              disabled={disabled}
              disabledShowTime={true}
              strategyOverlay="fixed"
              placementOverlay="top"
              placeholder="–"
            />
          )}
          {isDetailVm && vm.isEditable && (
            <div>
              <Check
                target={vm}
                property="deactivateImmediately"
                label={vm.translate("isImmediateCancellation")}
                className={`${vm.deactivateImmediately && vm.isValidSinceInFuture ? "mt-3 mb-2" : "mt-3 mb-4"}`}
              />
              {vm.deactivateImmediately && vm.isValidSinceInFuture && (
                <div className="text-danger small">{vm.translate("cannotDeactivateImmediatelyFutureValidSince")}</div>
              )}
            </div>
          )}
        </FormGroup>
      </Form.Row>
    </>
  );
});

export default EmailBlacklistForm;
